import { useEffect } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import type { Camera } from './camera-types'
import type { CameraLocationFormValues } from '@/lib/schemas/grower-camera-schemas'
import { GrowerSearchSelect } from '@/components/growers/grower-search-select'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useGrower } from '@/hooks/use-growers-query'
import { createCameraLocationSchema } from '@/lib/schemas/grower-camera-schemas'
import { m } from '@/lib/paraglide/messages'

interface Props {
    /** Camera đang chọn nơi đặt; null = hộp thoại đóng. */
    camera: Camera | null
    isSaving: boolean
    /** Gắn vào vùng `areaId`, hoặc `null` để gỡ khỏi vùng đang gắn. */
    onSave: (areaId: string | null) => Promise<unknown>
    onClose: () => void
}

/**
 * Chọn vùng trồng đặt camera: hộ → vùng của hộ đó. Mỗi camera chỉ ở một chỗ,
 * nên lưu là chuyển camera sang vùng mới; "Gỡ khỏi vùng" để camera không ở đâu
 * (camera vẫn còn, gắn lại lúc nào cũng được).
 */
export function CameraLocationDialog({
    camera,
    isSaving,
    onSave,
    onClose,
}: Props) {
    const form = useForm<CameraLocationFormValues>({
        resolver: zodResolver(createCameraLocationSchema()),
        defaultValues: { grower_id: '', area_id: '' },
    })

    useEffect(() => {
        if (!camera) return
        form.reset({
            grower_id: camera.grower_area?.grower_id ?? '',
            area_id: camera.grower_area?.area_id ?? '',
        })
    }, [camera, form])

    const growerId = useWatch({ control: form.control, name: 'grower_id' })
    const areaId = useWatch({ control: form.control, name: 'area_id' })
    const { data: grower } = useGrower(growerId || undefined)
    const areas = (grower?.areas ?? []).flatMap((a) =>
        a.id ? [{ id: a.id, name: a.name, code: a.code ?? '' }] : [],
    )

    // Lỗi lưu đã được hook toast; bắt lại để promise không reject ra ngoài.
    const submit = form.handleSubmit(async (values) => {
        await onSave(values.area_id).catch(() => undefined)
    })

    return (
        <Dialog
            open={Boolean(camera)}
            onOpenChange={(open) => !open && onClose()}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{m.camera_location_title()}</DialogTitle>
                    <DialogDescription>
                        {camera
                            ? m.camera_location_desc({ name: camera.name })
                            : null}
                    </DialogDescription>
                </DialogHeader>
                <FormProvider {...form}>
                    <form
                        noValidate
                        className="space-y-4"
                        onSubmit={(e) => void submit(e)}
                    >
                        <Field
                            name="grower_id"
                            label={m.camera_location_grower()}
                            required
                        >
                            <GrowerSearchSelect
                                id="grower_id"
                                value={growerId}
                                fallbackLabel={camera?.grower_area?.grower_name}
                                onChange={(id) => {
                                    form.setValue('grower_id', id, {
                                        shouldValidate:
                                            form.formState.isSubmitted,
                                    })
                                    // Đổi hộ thì vùng cũ không còn thuộc hộ mới.
                                    form.setValue('area_id', '')
                                }}
                            />
                        </Field>
                        <Field
                            name="area_id"
                            label={m.camera_location_area()}
                            required
                        >
                            <Select
                                value={areaId}
                                onValueChange={(v) =>
                                    form.setValue('area_id', v, {
                                        shouldValidate:
                                            form.formState.isSubmitted,
                                    })
                                }
                            >
                                <SelectTrigger
                                    id="area_id"
                                    aria-label={m.camera_location_area()}
                                    className="w-full"
                                >
                                    <SelectValue
                                        placeholder={m.camera_location_area_placeholder()}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {areas.map((a) => (
                                        <SelectItem key={a.id} value={a.id}>
                                            {a.code
                                                ? `${a.name} (${a.code})`
                                                : a.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <DialogFooter className="gap-2 sm:justify-between">
                            {camera?.grower_area ? (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    disabled={isSaving}
                                    onClick={() =>
                                        void onSave(null).catch(() => undefined)
                                    }
                                >
                                    {m.camera_location_remove()}
                                </Button>
                            ) : (
                                <span />
                            )}
                            <Button type="submit" disabled={isSaving}>
                                {m.camera_location_save()}
                            </Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}
